/**
 * Multitrack mic files (amendment 24): one polyphonic WAV with one channel per mic, as the RØDECaster Pro II, the
 * Zoom PodTrak and Tascam recorders write it.
 *
 * Pure functions shared by the API (which reads the header and a sample of the file with Range requests after an
 * upload completes), the web app (levels, Skip defaults, the device presets) and the tests. Nothing here knows a
 * device: silence and duplicates are detected from the samples, and the presets only suggest labels.
 */

/** A mic whose first audio stream has more than this many channels is a multitrack and needs a decision. */
export const MULTITRACK_MIN_CHANNELS = 3;
/** Below this RMS over the sampled windows a channel is `silent` (an unused input). */
export const CHANNEL_SILENT_DBFS = -60;
/** Two channels are duplicates when they correlate at least this much… */
export const CHANNEL_DUPLICATE_CORRELATION = 0.98;
/** …and their levels differ by at most this many dB (a mono input written to a stereo pair, the mix's L and R). */
export const CHANNEL_DUPLICATE_MAX_LEVEL_DB = 1;
/** A channel that correlates at least this much with the sum of the other channels is a mix of them. */
export const CHANNEL_MIX_CORRELATION = 0.9;
/** The summary samples this many windows spread over the file… */
export const CHANNEL_SUMMARY_WINDOWS = 30;
/** …of this length each (about 26 MB of reads for a 6-channel 24-bit file of any length). */
export const CHANNEL_SUMMARY_WINDOW_MS = 1000;
/** Length of each channel's preview clip in the upload form. */
export const CHANNEL_PREVIEW_MS = 10_000;
/** Bytes of a WAV header the API reads first (fmt, ds64, bext and iXML chunks come before `data`). */
export const WAV_HEADER_PROBE_BYTES = 256 * 1024;
/** Never sample more channels than this (the covariance matrix grows with the square). */
export const MAX_SUMMARY_CHANNELS = 64;
/** Hard budget of one summary (coordinator condition 4a): bytes read from storage, header and previews included… */
export const CHANNEL_SUMMARY_MAX_BYTES = 64 * 1024 * 1024;
/** …and wall time; past it the summary is abandoned with `reason: 'timeout'` and the form works without meters. */
export const CHANNEL_SUMMARY_MAX_MS = 15_000;

/**
 * Error codes of the multitrack routes (amendment 24): `multitrack_undecided` from `POST /v1/sessions/{id}/process`
 * while a multitrack mic is neither split nor kept as a mixdown; the others from `POST /v1/sources/{id}/split`.
 */
export const MULTITRACK_ERRORS = ['multitrack_undecided', 'processing_started', 'upload_incomplete', 'channels_unknown'] as const;
export type MultitrackError = (typeof MULTITRACK_ERRORS)[number];

export const MULTITRACK_DECISIONS = ['pending', 'split', 'mixdown'] as const;
export type MultitrackDecision = (typeof MULTITRACK_DECISIONS)[number];

export interface ChannelStat {
  /** 0-based channel of the first audio stream. */
  channel: number;
  rms_dbfs: number;
  peak_dbfs: number;
  silent: boolean;
  /** The lower-numbered channel this one is identical or near-identical to, else null. */
  duplicate_of: number | null;
  /** The channel is (close to) the sum of the other channels: a stereo or main mix, not a mic. */
  mix: boolean;
}

export interface ChannelSummary {
  method: 'sampled' | 'full';
  windows: number;
  window_ms: number;
  sample_rate: number;
  bits_per_sample: number;
  channels: ChannelStat[];
}

export interface WavFormat {
  container: 'RIFF' | 'RF64' | 'BW64';
  /** 1 = integer PCM, 3 = IEEE float (WAVE_FORMAT_EXTENSIBLE resolved to its sub-format). */
  format: 1 | 3;
  channels: number;
  sampleRate: number;
  bitsPerSample: number;
  blockAlign: number;
  dataOffset: number;
  dataSize: number;
}

export type WavHeaderResult = { ok: true; wav: WavFormat } | { ok: false; reason: 'not_wav' | 'unsupported_format' | 'header_too_large' | 'truncated' | 'inconsistent' };

const ascii = (b: Uint8Array, at: number) => String.fromCharCode(b[at], b[at + 1], b[at + 2], b[at + 3]);

/**
 * Parse a WAV/BWF/RF64 header from the first bytes of a file. `fileSize` corrects a `data` size that a recorder
 * left at 0 or 0xFFFFFFFF (an unfinalised file, or RF64 without ds64).
 *
 * The bytes are customer-controlled (coordinator condition 4b): every size is checked against the bytes read and the
 * object size, nothing is allocated from a header field, and a lying header gives `{ ok: false }`, never a throw.
 */
export function parseWavHeader(bytes: Uint8Array, fileSize?: number): WavHeaderResult {
  try {
    return parseWavHeaderUnsafe(bytes, fileSize);
  } catch {
    return { ok: false, reason: 'inconsistent' };
  }
}

function parseWavHeaderUnsafe(bytes: Uint8Array, fileSize?: number): WavHeaderResult {
  const limit = fileSize ?? Number.MAX_SAFE_INTEGER;
  if (fileSize !== undefined && (!Number.isSafeInteger(fileSize) || fileSize < bytes.length)) return { ok: false, reason: 'inconsistent' };
  if (bytes.length < 12) return { ok: false, reason: 'truncated' };
  const riff = ascii(bytes, 0);
  if ((riff !== 'RIFF' && riff !== 'RF64' && riff !== 'BW64') || ascii(bytes, 8) !== 'WAVE') return { ok: false, reason: 'not_wav' };
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 12;
  let fmt: Omit<WavFormat, 'container' | 'dataOffset' | 'dataSize'> | null = null;
  let ds64Data: number | null = null;
  while (pos + 8 <= bytes.length) {
    const id = ascii(bytes, pos);
    const size = dv.getUint32(pos + 4, true);
    const body = pos + 8;
    if (id !== 'data' && body + size > limit) return { ok: false, reason: 'inconsistent' }; // a chunk past the end of the object
    if (id === 'ds64' && size >= 16 && body + 16 <= bytes.length) {
      const big = dv.getBigUint64(body + 8, true);
      if (big > BigInt(Number.MAX_SAFE_INTEGER)) return { ok: false, reason: 'inconsistent' };
      ds64Data = Number(big);
    } else if (id === 'fmt ') {
      if (size < 16) return { ok: false, reason: 'unsupported_format' };
      if (body + 16 > bytes.length) return { ok: false, reason: 'truncated' };
      let code = dv.getUint16(body, true);
      const channels = dv.getUint16(body + 2, true);
      const sampleRate = dv.getUint32(body + 4, true);
      const blockAlign = dv.getUint16(body + 12, true);
      const bitsPerSample = dv.getUint16(body + 14, true);
      if (code === 0xfffe) {
        if (size < 40 || body + 26 > bytes.length) return { ok: false, reason: 'truncated' };
        code = dv.getUint16(body + 24, true); // first two bytes of the sub-format GUID
      }
      if ((code !== 1 && code !== 3) || !channels || !sampleRate || !blockAlign) return { ok: false, reason: 'unsupported_format' };
      if (code === 1 && ![8, 16, 24, 32].includes(bitsPerSample)) return { ok: false, reason: 'unsupported_format' };
      if (code === 3 && bitsPerSample !== 32) return { ok: false, reason: 'unsupported_format' };
      if (blockAlign !== channels * (bitsPerSample / 8)) return { ok: false, reason: 'unsupported_format' };
      if (sampleRate < 1000 || sampleRate > 768_000) return { ok: false, reason: 'unsupported_format' };
      fmt = { format: code as 1 | 3, channels, sampleRate, bitsPerSample, blockAlign };
    } else if (id === 'data') {
      if (!fmt) return { ok: false, reason: 'unsupported_format' };
      let dataSize = size === 0xffffffff && ds64Data !== null ? ds64Data : size;
      if (fileSize !== undefined && (dataSize === 0 || dataSize === 0xffffffff || body + dataSize > fileSize)) dataSize = Math.max(0, fileSize - body);
      dataSize = Math.min(dataSize, Math.max(0, limit - body));
      dataSize -= dataSize % fmt.blockAlign;
      if (dataSize <= 0) return { ok: false, reason: 'truncated' };
      return { ok: true, wav: { container: riff as WavFormat['container'], ...fmt, dataOffset: body, dataSize } };
    }
    pos = body + size + (size % 2);
  }
  return { ok: false, reason: 'header_too_large' };
}

export interface ChannelReadPlan {
  windows: number;
  windowMs: number;
  /** Distinct 10 s (or shorter) preview reads; 0 = no previews (the file is too wide for the budget). */
  previewReads: number;
  previewMs: number;
  /** Bytes the plan reads in total, header included (always ≤ the budget). */
  bytes: number;
}

/**
 * Fit the summary's reads into `budget` bytes (condition 4a): fewer preview reads, then shorter windows, then
 * shorter previews, then fewer windows, then no previews at all. Never reads more than the budget.
 */
export function planChannelReads(wav: Pick<WavFormat, 'channels' | 'sampleRate' | 'blockAlign' | 'dataSize'>, budget = CHANNEL_SUMMARY_MAX_BYTES, headerBytes = WAV_HEADER_PROBE_BYTES): ChannelReadPlan {
  const perMs = (wav.sampleRate * wav.blockAlign) / 1000;
  const fileMs = wav.dataSize / perMs;
  const p = { windows: CHANNEL_SUMMARY_WINDOWS, windowMs: CHANNEL_SUMMARY_WINDOW_MS, previewReads: Math.min(wav.channels, 6), previewMs: CHANNEL_PREVIEW_MS };
  const cost = () => {
    const sample = Math.min(wav.dataSize, Math.ceil(p.windows * p.windowMs * perMs));
    const preview = p.previewReads * Math.ceil(Math.min(p.previewMs, fileMs) * perMs);
    return headerBytes + sample + preview;
  };
  const steps: (() => boolean)[] = [
    () => (p.previewReads > 1 ? (p.previewReads--, true) : false),
    () => (p.windowMs > 250 ? ((p.windowMs = Math.max(250, p.windowMs / 2)), true) : false),
    () => (p.previewMs > 3000 ? ((p.previewMs = Math.max(3000, p.previewMs - 2000)), true) : false),
    () => (p.windows > 10 ? ((p.windows = Math.max(10, p.windows - 5)), true) : false),
    () => (p.previewReads > 0 ? ((p.previewReads = 0), true) : false),
  ];
  for (const step of steps) while (cost() > budget && step());
  return { ...p, bytes: cost() };
}

/** Byte ranges (inclusive end, like an HTTP Range) of the windows the summary reads; the whole data when it is short. */
export function summaryWindows(wav: WavFormat, windows = CHANNEL_SUMMARY_WINDOWS, windowMs = CHANNEL_SUMMARY_WINDOW_MS): { start: number; end: number; at_ms: number }[] {
  const frames = wav.dataSize / wav.blockAlign;
  const perWindow = Math.max(1, Math.round((wav.sampleRate * windowMs) / 1000));
  if (frames <= perWindow * windows) {
    return frames > 0 ? [{ start: wav.dataOffset, end: wav.dataOffset + wav.dataSize - 1, at_ms: 0 }] : [];
  }
  const out = [];
  const stride = (frames - perWindow) / (windows - 1);
  for (let i = 0; i < windows; i++) {
    const f = Math.round(i * stride);
    const start = wav.dataOffset + f * wav.blockAlign;
    out.push({ start, end: start + perWindow * wav.blockAlign - 1, at_ms: Math.round((f * 1000) / wav.sampleRate) });
  }
  return out;
}

/** Decode interleaved PCM into one Float32Array per channel, values in [-1, 1]. */
export function deinterleave(bytes: Uint8Array, wav: Pick<WavFormat, 'format' | 'channels' | 'bitsPerSample' | 'blockAlign'>): Float32Array[] {
  const frames = Math.floor(bytes.length / wav.blockAlign);
  const out = Array.from({ length: wav.channels }, () => new Float32Array(frames));
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const bps = wav.bitsPerSample / 8;
  for (let f = 0; f < frames; f++) {
    const base = f * wav.blockAlign;
    for (let c = 0; c < wav.channels; c++) {
      const o = base + c * bps;
      let v: number;
      if (wav.format === 3) v = dv.getFloat32(o, true);
      else if (bps === 2) v = dv.getInt16(o, true) / 32768;
      else if (bps === 3) {
        const x = bytes[o] | (bytes[o + 1] << 8) | (bytes[o + 2] << 16);
        v = ((x & 0x800000 ? x - 0x1000000 : x) as number) / 8388608;
      } else if (bps === 4) v = dv.getInt32(o, true) / 2147483648;
      else v = (bytes[o] - 128) / 128;
      out[c][f] = v;
    }
  }
  return out;
}

const db = (x: number) => (x > 0 ? Math.round(20 * Math.log10(x) * 10) / 10 : -120);

/**
 * Running per-channel statistics over the sampled windows: sum of squares, peak and the covariance matrix
 * (correlations every 2nd frame, which is plenty for duplicates and mixes and halves the cost for 16 channels).
 */
export class ChannelAccumulator {
  readonly channels: number;
  private readonly sumSq: Float64Array;
  private readonly peak: Float64Array;
  /** Upper triangle (i ≤ j) of Σ x_i·x_j over the decimated frames. */
  private readonly cov: Float64Array;
  private frames = 0;
  windows = 0;

  constructor(channels: number) {
    this.channels = channels;
    this.sumSq = new Float64Array(channels);
    this.peak = new Float64Array(channels);
    this.cov = new Float64Array(channels * channels);
  }

  add(chans: Float32Array[]): void {
    const n = chans[0]?.length ?? 0;
    const C = this.channels;
    for (let c = 0; c < C; c++) {
      const x = chans[c];
      let s = 0;
      let p = this.peak[c];
      for (let f = 0; f < n; f++) {
        const v = x[f];
        s += v * v;
        const a = v < 0 ? -v : v;
        if (a > p) p = a;
      }
      this.sumSq[c] += s;
      this.peak[c] = p;
    }
    for (let i = 0; i < C; i++) {
      const xi = chans[i];
      for (let j = i; j < C; j++) {
        const xj = chans[j];
        let s = 0;
        for (let f = 0; f < n; f += 2) s += xi[f] * xj[f];
        this.cov[i * C + j] += s;
      }
    }
    this.frames += n;
    this.windows++;
  }

  private c(i: number, j: number) {
    return i <= j ? this.cov[i * this.channels + j] : this.cov[j * this.channels + i];
  }

  /** Per-channel stats with `silent`, `duplicate_of` and `mix` resolved. */
  stats(): ChannelStat[] {
    const C = this.channels;
    const rms = Array.from(this.sumSq, (s) => (this.frames ? Math.sqrt(s / this.frames) : 0));
    const out: ChannelStat[] = rms.map((r, channel) => {
      const rms_dbfs = db(r);
      return { channel, rms_dbfs, peak_dbfs: db(this.peak[channel]), silent: rms_dbfs < CHANNEL_SILENT_DBFS, duplicate_of: null, mix: false };
    });
    const corr = (i: number, j: number) => {
      const d = Math.sqrt(this.c(i, i) * this.c(j, j));
      return d > 0 ? this.c(i, j) / d : 0;
    };
    for (let j = 0; j < C; j++) {
      if (out[j].silent) continue;
      for (let i = 0; i < j; i++) {
        if (out[i].silent || out[i].duplicate_of !== null) continue;
        if (corr(i, j) >= CHANNEL_DUPLICATE_CORRELATION && Math.abs(out[i].rms_dbfs - out[j].rms_dbfs) <= CHANNEL_DUPLICATE_MAX_LEVEL_DB) {
          out[j].duplicate_of = i;
          break;
        }
      }
    }
    // A mix is explained by the sum of the other representative channels (not silent, not a duplicate):
    // corr(c, Σ others) = Σ_k cov(c,k) / sqrt(var(c) · Σ_k Σ_l cov(k,l)). A mic with bleed stays well below.
    const reps = out.filter((s) => !s.silent && s.duplicate_of === null).map((s) => s.channel);
    if (reps.length >= 3) {
      for (const ch of reps) {
        const others = reps.filter((k) => k !== ch);
        let num = 0;
        let varS = 0;
        for (const k of others) {
          num += this.c(ch, k);
          for (const l of others) varS += this.c(k, l);
        }
        const d = Math.sqrt(this.c(ch, ch) * varS);
        if (d > 0 && num / d >= CHANNEL_MIX_CORRELATION) out[ch].mix = true;
      }
    }
    return out;
  }

  /** Mean square of one channel in one window (for picking each channel's preview), computed by the caller. */
  static meanSquare(x: Float32Array): number {
    let s = 0;
    for (let f = 0; f < x.length; f++) s += x[f] * x[f];
    return x.length ? s / x.length : 0;
  }
}

/** The channels the upload form pre-sets to Skip: silent ones, duplicates and mixes. */
export function defaultSkipped(stats: Pick<ChannelStat, 'silent' | 'duplicate_of' | 'mix'>): boolean {
  return stats.silent || stats.duplicate_of !== null || stats.mix;
}

/** A mono 16-bit PCM WAV file (the channel preview clips). */
export function monoWav(samples: Float32Array, sampleRate: number): Uint8Array {
  const data = samples.length * 2;
  const out = new Uint8Array(44 + data);
  const dv = new DataView(out.buffer);
  const w = (at: number, s: string) => {
    for (let i = 0; i < 4; i++) out[at + i] = s.charCodeAt(i);
  };
  w(0, 'RIFF');
  dv.setUint32(4, 36 + data, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  dv.setUint32(16, 16, true);
  dv.setUint16(20, 1, true);
  dv.setUint16(22, 1, true);
  dv.setUint32(24, sampleRate, true);
  dv.setUint32(28, sampleRate * 2, true);
  dv.setUint16(32, 2, true);
  dv.setUint16(34, 16, true);
  w(36, 'data');
  dv.setUint32(40, data, true);
  for (let i = 0; i < samples.length; i++) {
    const v = Math.max(-1, Math.min(1, samples[i]));
    dv.setInt16(44 + i * 2, v < 0 ? Math.round(v * 32768) : Math.round(v * 32767), true);
  }
  return out;
}

/** Is this mic source a multitrack that still needs a decision (split, or kept as a mixdown)? */
export function multitrackPending(src: { kind: string; channel?: number | null; media_info?: Record<string, unknown> | null }): boolean {
  if (src.kind !== 'mic' || (src.channel !== null && src.channel !== undefined)) return false;
  const mi = (src.media_info ?? {}) as { channels?: unknown; multitrack?: { decision?: unknown } };
  return typeof mi.channels === 'number' && mi.channels >= MULTITRACK_MIN_CHANNELS && mi.multitrack?.decision !== 'mixdown';
}

// ───────────────────────────── device presets: suggested labels only ─────────────────────────────

export interface ChannelPreset {
  id: string;
  /** Shown in the preset picker. */
  name: string;
  /** Label suggested for a 0-based channel, or null when the preset says nothing about it. */
  label: (channel: number, channels: number) => string | null;
}

/**
 * RØDECaster Pro II multitrack layouts (RØDE help centre, "The RØDECaster Pro II / Duo Multitrack Channel Layout",
 * and the RØDE multitrack guides). Firmware 1.7.3 and later: channels 1–2 are the main stereo mix, then every
 * fader in mixer order as a stereo pair (a mono mic appears on both channels of its pair). Before 1.7.3: main mix
 * L/R, then Combo 1–4 as mono channels. Labels only: processing never relies on them.
 */
export const CHANNEL_PRESETS: readonly ChannelPreset[] = [
  {
    id: 'rodecaster-pro-2',
    name: 'RØDECaster Pro II (firmware 1.7.3+)',
    label: (c) => {
      const pair = Math.floor(c / 2);
      const range = `Ch ${pair * 2 + 1}–${pair * 2 + 2}`;
      if (pair === 0) return `${range}: Main mix`;
      return pair <= 4 ? `${range}: Fader ${pair} (Combo ${pair})` : `${range}: Fader ${pair}`;
    },
  },
  {
    id: 'rodecaster-pro-2-legacy',
    name: 'RØDECaster Pro II (before 1.7.3)',
    label: (c) => (c === 0 ? 'Ch 1: Main mix L' : c === 1 ? 'Ch 2: Main mix R' : c <= 5 ? `Ch ${c + 1}: Combo ${c - 1}` : null),
  },
];

export function presetLabel(presetId: string | null | undefined, channel: number, channels: number): string | null {
  const p = CHANNEL_PRESETS.find((x) => x.id === presetId);
  return p ? p.label(channel, channels) : null;
}
